import React, { useState, useEffect } from "react";
import * as yup from "yup";
import moment from "moment";
// Material ui
import {
  Typography,
  Grid,
  DialogActions,
  Button,
  CircularProgress,
  makeStyles,
  MenuItem,
} from "@material-ui/core";
import { KeyboardDatePicker } from "@material-ui/pickers";
// Formik
import { Formik, Form } from "formik";
// Components
import CustomTextField from "../Formik/CustomTextField";
import CustomSelect from "../Formik/CustomSelect";
// Utils
import { TabletFormType } from "./FormTypes";
import useCompanyPage from "../../hooks/useCompanyPage";
import { HandleDeviceSubmit } from "../Modals/AddDevice";
import { BasicDeviceInfo } from "../../@types/computer";

interface Props {
  onSubmit: HandleDeviceSubmit;
  onClose?: () => void;
  device?: BasicDeviceInfo;
}

const useStyles = makeStyles((theme) => ({
  textField: { marginBottom: theme.spacing(1.5) },
}));

const TabletForm: React.FC<Props> = ({ onSubmit, onClose, device }) => {
  // Hooks
  const classes = useStyles();
  const company = useCompanyPage();

  // States
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const availableOs = ["Android", "iPadOS"];

  const getSpecificValue = (name: string) =>
    device?.specificInformation?.reduce((acc, curr) => (curr.name === name ? curr.value : acc), "");

  const initialState: TabletFormType = {
    name: device?.name || "",
    model: device?.model || "",
    brand: device?.brand || "",
    os: getSpecificValue("OS") || availableOs[0],
    osVersion: getSpecificValue("OS version") || "",
    serialNumber: device?.serialNumber || "",
    warrantyDate: device?.warrantyDate ? moment(device.warrantyDate) : null,
    comments: device?.comments || "",
  };

  const validationSchema = yup.object({
    name: yup.string().required("Device name is required"),
    model: yup.string().required("Model is required"),
    brand: yup.string().required("Brand is required"),
    os: yup.string().required("OS is required").oneOf(availableOs),
    osVersion: yup.string(),
    warrantyDate: yup.date().nullable(),
    serialNumber: yup.string(),
    comment: yup.string(),
  });

  useEffect(() => setIsEditing(device ? true : false), [device]);

  const handleSubmit = (data: TabletFormType) => {
    const requestData = {
      deviceType: "tablet",
      device: {
        ...data,
        companyId: company._id,
        warrantyDate: data.warrantyDate?.toDate() || null,
        specificInformation: [
          { name: "OS", value: data.os },
          { name: "OS version", value: data.osVersion },
        ].filter((info) => info.value.trim() !== ""),
      },
    };

    // @ts-ignore
    delete requestData.device.os;
    // @ts-ignore
    delete requestData.device.osVersion;

    onSubmit(requestData, setIsSubmitting);
  };

  return (
    <>
      <Typography variant="h6" className="text-center">
        {!isEditing ? "Add a new tablet" : `Edit ${device?.name}`}
      </Typography>

      <Formik
        initialValues={initialState}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => (
          <Form>
            <CustomTextField
              label="Device name"
              name="name"
              fullWidth
              required
              className={classes.textField}
              disabled={isSubmitting}
            />

            <Grid container spacing={2} className={classes.textField}>
              <Grid item xs={12} sm>
                <CustomTextField
                  label="Model"
                  name="model"
                  fullWidth
                  required
                  disabled={isSubmitting}
                />
              </Grid>

              <Grid item xs={12} sm>
                <CustomTextField
                  label="Brand"
                  name="brand"
                  fullWidth
                  required
                  disabled={isSubmitting}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} className={classes.textField}>
              <Grid item xs={12} sm>
                <CustomSelect name="os" id="os-name" label="OS" required disabled={isSubmitting}>
                  {availableOs.map((os) => (
                    <MenuItem key={os} value={os}>
                      {os}
                    </MenuItem>
                  ))}
                </CustomSelect>
              </Grid>

              <Grid item xs={12} sm>
                <CustomTextField
                  label="Version (optional)"
                  name="osVersion"
                  fullWidth
                  disabled={isSubmitting}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} className={classes.textField}>
              <Grid item xs={12} sm>
                <CustomTextField
                  label="Serial number (optional)"
                  name="serialNumber"
                  fullWidth
                  disabled={isSubmitting}
                />
              </Grid>

              <Grid item xs={12} sm>
                <KeyboardDatePicker
                  label="Warranty date (optional)"
                  name="warrantyDate"
                  variant="inline"
                  format="DD/MM/yyyy"
                  autoOk
                  value={values.warrantyDate}
                  onChange={(value) => setFieldValue("warrantyDate", value)}
                  fullWidth
                  disabled={isSubmitting}
                />
              </Grid>
            </Grid>

            <CustomTextField
              label="Comments (optional)"
              name="comments"
              multiline={true}
              fullWidth
              className={classes.textField}
              disabled={isSubmitting}
              helperText="OBS: we support markdown text"
            />

            <DialogActions>
              <Button onClick={onClose}>Cancel</Button>

              <Button type="submit" disabled={isSubmitting}>
                {!isEditing ? "Add" : "Edit"}
              </Button>

              {isSubmitting && <CircularProgress />}
            </DialogActions>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default TabletForm;
